import { useEffect } from 'react';
import { useOrderDetailDrawerStore } from '../../stores/order-detail-drawer-store';
import { OrderDetailView } from './OrderDetailView';

/**
 * Overlay-drawer voor order-detail. Globaal gemount in AppLayout, zodat elke
 * page `useOrderDetailDrawerStore.open(id)` kan aanroepen.
 *
 * Stacking-tiers (zie ook AppointmentScheduler, WasserijItemDrawerPanel,
 * CancelOrderDialog, Upsell*Dialog, InvoiceDetailDrawerPanel):
 *   - Page modals (offerte, etc.):           z-40 / z-50
 *   - Order-drawer (deze):                    z-[55] / z-[60]
 *   - Modals/drawers vanuit order-drawer:    z-[65] / z-[70]
 *     (AppointmentScheduler, WasserijItem-drawer, Cancel/Upsell-dialogs)
 *   - Invoice-drawer:                         z-[70] / z-[80]
 */
export function OrderDetailDrawerPanel() {
  const openOrderId = useOrderDetailDrawerStore((s) => s.openOrderId);
  const close = useOrderDetailDrawerStore((s) => s.close);

  useEffect(() => {
    if (!openOrderId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [openOrderId, close]);

  if (!openOrderId) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/30 z-[55]"
        onClick={close}
        aria-hidden
      />
      <aside
        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-[60] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Order detail"
      >
        <OrderDetailView
          key={openOrderId}
          orderId={openOrderId}
          variant="drawer"
          onClose={close}
        />
      </aside>
    </>
  );
}
